import { useEffect, useRef, useState } from 'react';

import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import { previewBespokeTemplatePdfBespokeTemplatesPreviewPdfPost } from '../../services/api/sdk.gen';

/**
 * Milliseconds of quiet before the print preview re-renders.
 *
 * Each render is an HTTP round trip plus a ReportLab pass, so it waits for the
 * layout to settle. Layouts are pushed by the Forms Manager MCP rather than
 * hand-typed, so in practice this fires once per push.
 */
const RENDER_DEBOUNCE_MS = 1000;

type PrintPreviewProps = {
  formType: RogerDocFormType;
  schemaVersionName: string;
  formSchemaVersion: string;
  sectionName: string;
  /** Working-draft layout — saved or not; `null` while the JSON is invalid. */
  layout: Record<string, unknown>[] | null;
};

/**
 * The section's layout as it prints in a visit PDF.
 *
 * Visit PDFs are rendered server-side with ReportLab, not by printing the
 * React form, so this asks the backend for the real PDF rather than styling
 * the on-screen preview to look like paper.
 */
export function PrintPreview({
  formType,
  schemaVersionName,
  formSchemaVersion,
  sectionName,
  layout,
}: PrintPreviewProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  // Held in a ref as well so the revoke on unmount sees the current URL
  // without making the effect depend on it (which would re-fetch).
  const urlRef = useRef<string | null>(null);

  const layoutKey = layout == null ? null : JSON.stringify(layout);

  useEffect(() => {
    if (layoutKey == null) return;

    let cancelled = false;
    setLoading(true);
    setFailed(false);

    const timer = setTimeout(() => {
      void (async () => {
        try {
          const response =
            await previewBespokeTemplatePdfBespokeTemplatesPreviewPdfPost({
              body: {
                form_type: formType,
                schema_version_name: schemaVersionName,
                form_schema_version: formSchemaVersion,
                section_name: sectionName,
                layout_json: JSON.parse(layoutKey),
              },
              responseType: 'blob',
            });
          if (cancelled) return;
          if (response.error || !response.data) {
            throw new Error('Print preview request failed');
          }
          const next = URL.createObjectURL(response.data as Blob);
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          urlRef.current = next;
          setPdfUrl(next);
        } catch (error) {
          if (cancelled) return;
          console.error('Failed to render print preview', error);
          setFailed(true);
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, RENDER_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [layoutKey, formType, schemaVersionName, formSchemaVersion, sectionName]);

  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    []
  );

  if (layoutKey == null) {
    return (
      <div className='p-6 text-sm text-muted-foreground'>
        Fix the layout JSON to see the print view.
      </div>
    );
  }

  if (failed && !pdfUrl) {
    return (
      <div className='p-6 text-sm text-muted-foreground'>
        Unable to render the print view. Please try again.
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className='p-6 text-sm text-muted-foreground'>
        Rendering print view…
      </div>
    );
  }

  return (
    <div className='relative h-full'>
      <iframe
        title='Print view preview'
        src={pdfUrl}
        className='block h-full min-h-[40rem] w-full bg-background'
      />
      {failed ? (
        <div className='absolute right-3 top-3 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive shadow'>
          Could not update — showing the last render.
        </div>
      ) : (
        loading && (
          <div className='absolute right-3 top-3 rounded bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow'>
            Updating…
          </div>
        )
      )}
    </div>
  );
}
